import UndoEntry from './UndoEntry';
import otWhiteboard from '@aha-app/ot-whiteboard';
import Debug from 'debug';
import type WhiteboardController from '../controllers/WhiteboardController';
import { type OtWhiteboardOp } from '../ot-whiteboard';
import type ClientBase from './ClientBase';

const debug = Debug('whiteboard:undo');
const debugStack = Debug('whiteboard:undoStack');

// Manage the undo stack. We store locally generated operations and allow
// them to be reversed to cause an undo.
//
// In a collaborative environment undo is much harder than it would initially
// appear. The undo actions must take into account any remote changes that
// happened subsequently since those operations that would affect how the undo
// should be applied.
//
// Read this blog https://www.aha.io/blog/text-editor before trying to
// understand this code.
//
// Care needs to be taken with collecting operations for undo across asynchronous
// functions. The undo manager is global. `addUndo` should be used in any
// async code that may trigger operations.
class UndoManager {
  // We truncate the size of the undo stack for performance reasons. Since we
  // have to transform all incoming remote operations against the undo
  // stack there is significant cost to a big stack. So we need to limit
  // how far back a user can go. We use a high limit, more than someone could
  // every hit Cmd-Z for.
  UNDO_STACK_SIZE_LIMIT = 200;

  // Stack of undo/redo actions.
  undoStack: UndoEntry[] = [];
  redoStack: UndoEntry[] = [];

  // The undo entry that is currently being built up. Should always exist.
  entryInProgress = new UndoEntry();

  ignoringUndo = false;
  addingUndo: UndoEntry | null = null;

  constructor(
    private readonly controller: WhiteboardController,
    private readonly client: ClientBase
  ) {}

  reset() {
    this.undoStack = [];
    this.redoStack = [];
    this.entryInProgress = new UndoEntry();
  }

  currentUndoEntry() {
    return this.entryInProgress;
  }

  lastUndoEntry() {
    return this.undoStack[this.undoStack.length - 1];
  }

  // Prepare the canvas for an undo/redo operation.
  prepareForUndoRedo() {
    // Remove the current selection.
    this.controller.canvas.discardActiveObject();

    // Flush any pending operations to the remote so that they can't happen
    // out of order with the undo.
    this.client.sendPendingOperations();
  }

  // Save an operation to the undo stack.
  saveOp(op) {
    if (this.ignoringUndo) return;

    this.entryInProgress.addOperations(op);
  }

  // Perform any options in `func` without undo. `func` must not be async.
  withoutUndo(func: () => void) {
    try {
      this.ignoringUndo = true;
      func();
    } finally {
      this.ignoringUndo = false;
    }
  }

  // Operations performed within func will be added to an existing undoEntry.
  // This is necessary when operations may be separated in time by other
  // unrelated operations. `func` must not be async. `addUndo` can be reentered.
  addUndo(undoEntry: UndoEntry, func: () => void) {
    if (!undoEntry) {
      console.error('No undo entry provided to addUndo');
    }
    if (this.addingUndo) {
      // We are already adding undo, just call the function.
      func();
      return;
    }

    this.addingUndo = this.entryInProgress;
    if (undoEntry) {
      this.entryInProgress = undoEntry;
    }
    try {
      func();
    } finally {
      this.entryInProgress = this.addingUndo;
      this.addingUndo = null;
    }
  }

  // Remove an undo entry.
  removeUndo(entry: UndoEntry) {
    if (entry === this.entryInProgress) {
      this.entryInProgress = new UndoEntry();
    }
    this.undoStack = this.undoStack.filter(e => e !== entry);
    this.redoStack = this.redoStack.filter(e => e !== entry);
    this.updateUndoRedoState();
  }

  // A remote operation was received. For now we do the most naive implementation
  // and transform the entire undo and redo stacks by the incoming operation.
  remoteOp(remoteOp: OtWhiteboardOp[]) {
    if (!this.entryInProgress.empty()) {
      // Transform the pending operations which are not on the stack yet.
      const [left, right] = otWhiteboard.type.transformX(
        this.entryInProgress.entryOps,
        remoteOp
      );
      this.entryInProgress.entryOps = left;

      // Return the transformed remote operations.
      remoteOp = right;
    }

    // Then transform the items on the stacks.
    debug(`Transforming undo stack with ${JSON.stringify(remoteOp)}`);
    this.undoStack = this.transformStack(this.undoStack, remoteOp);
    this.redoStack = this.transformStack(this.redoStack, remoteOp);
    this.debugLogStacks();
    this.updateUndoRedoState();
  }

  // Apply incoming remote `op` against the undo or redo stack. As we work down
  // the stack we must also transform the remote operations against each undo
  // entry.
  //
  // The test "handles conflicting local and remote updates" demonstrates why
  // this is necessary.
  //
  // See https://github.com/aha-app/aha-editor/blob/8bbdde717c6112e98b7d65c19a5cecb8751208be/src/collaboration/GroupUndoStack.js#L153
  // for a comment describing this in more detail. I rediscovered it painfully
  // through debugging.
  transformStack(stack: UndoEntry[], remoteOp: OtWhiteboardOp[]) {
    for (let i = stack.length - 1; i >= 0; --i) {
      remoteOp = stack[i].transform(remoteOp);
    }

    // Remove any undo entries that are now empty.
    return stack.filter(entry => entry.entryOps.length > 0);
  }

  // Keep observable hasUndo / hasRedo state updated so we can re-render
  // components that depend on these values.
  updateUndoRedoState(): void {
    this.controller.actionUpdateUndoRedoState(
      this.undoStack.length > 0,
      this.redoStack.length > 0
    );
  }

  pushToUndoStack(undoEntry: UndoEntry): void {
    this.undoStack.push(undoEntry);
    this.updateUndoRedoState();
  }

  pushToRedoStack(redoEntry: UndoEntry): void {
    this.redoStack.push(redoEntry);
    this.updateUndoRedoState();
  }

  // Complete a group of related actions that should become one undo entry.
  completeUndoGroup() {
    if (!this.entryInProgress.empty()) {
      const undoEntry = this.entryInProgress;
      this.pushToUndoStack(undoEntry);

      // Truncate the stack if it got too big.
      while (this.undoStack.length > this.UNDO_STACK_SIZE_LIMIT) {
        debug(`Truncating undo stack`);
        this.undoStack.shift();
      }

      debug(`======= UNDO GROUP END (${undoEntry.entryOps.length} ops)`);
      debugStack(`Undo stack: ${this.undoStack.length}`);
      this.debugLogStacks();

      // Prepare to collect new operations.
      this.entryInProgress = new UndoEntry();

      // Clear the redo stack.
      this.clearRedoStack();
      this.updateUndoRedoState();

      return undoEntry;
    }
  }

  // Discard the last group of actions - i.e. because the action was aborted.
  discardUndoGroup() {
    this.entryInProgress = new UndoEntry();
  }

  // Undo the last operation.
  undo() {
    this.prepareForUndoRedo();

    // Make sure the last undo group is closed out.
    this.completeUndoGroup();

    const entry = this.undoStack.pop();
    if (!entry) return;

    debug(
      `Undoing (${this.redoStack.length}): ${JSON.stringify(entry.entryOps)}`
    );

    this.controller.aroundBulkAddingCode(() => entry.apply(this.client));
    this.pushToRedoStack(entry.reverse());
    this.debugLogStacks();
  }

  // Redo the last operation.
  redo() {
    this.prepareForUndoRedo();

    // Redo is not possible if the last action wasn't an undo.
    if (!this.entryInProgress.empty()) {
      this.completeUndoGroup();
      debug('Attempt to redo with incomplete undo group in progress');
      return;
    }

    const entry = this.redoStack.pop();
    if (!entry) return;

    debug(
      `Redoing (${this.redoStack.length}): ${JSON.stringify(entry.entryOps)}`
    );

    this.controller.aroundBulkAddingCode(() => entry.apply(this.client));
    this.pushToUndoStack(entry.reverse());
    this.debugLogStacks();
  }

  logStacks() {
    this.logStack(this.undoStack, 'Undo');
    this.logStack(this.redoStack, 'Redo');
  }

  logStack(stack: UndoEntry[], name: string) {
    for (let i = stack.length - 1; i >= 0; --i) {
      const entry = stack[i];
      console.log(`${name}[${i}] = ${JSON.stringify(entry.entryOps)}`);
    }
  }

  debugLogStacks() {
    debugStack('undo', this.undoStack);
    debugStack('redo', this.redoStack);
  }

  clearRedoStack(): void {
    this.redoStack = [];
  }
}

export default UndoManager;
